/* TOTOKA Theatre V2 refinement. Preserves the existing stage and orbit choreography.
   Native scrolling; no autoplay, timers, global wheel handlers or animation loop. */
(() => {
  'use strict';
  const SELECTOR = '[data-totoka-product-theatre-v2]:not([data-totoka-product-theatre-s26])';
  // Section scripts may be evaluated again by the Shopify Theme Editor.
  if (window.TotokaPdpTheatreV2) {
    window.TotokaPdpTheatreV2.init(document);
    return;
  }
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function init(root) {
    if (root._tpt2Destroy) return;
    const abort = new AbortController();
    const on = (target, type, callback, options = {}) =>
      target?.addEventListener(type, callback, { ...options, signal: abort.signal });
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const desktop = matchMedia('(min-width: 990px)');
    const pointer = matchMedia('(hover: hover) and (pointer: fine)');
    const story = root.querySelector('[data-tpt2-story]');
    const stage = root.querySelector('[data-tpt2-stage]');
    const chapters = [...root.querySelectorAll('[data-tpt2-chapter]')];
    const orbitItems = [...root.querySelectorAll('.tpt2__orbit-item')];
    let orbitState = null;
    const galleries = [...root.querySelectorAll('[data-tpt2-gallery]')];
    const observers = [];
    const hasObserver = 'IntersectionObserver' in window;
    let visible = !hasObserver;
    let frame = 0;
    let destroyed = false;
    let pointerPosition = null;

    function syncOrbit() {
      const next = visible && !document.hidden && !reduced.matches &&
        root.classList.contains('is-stage-visible') && root.dataset.activeChapter === '1'
        ? 'running' : 'paused';
      if (next === orbitState) return;
      orbitState = next;
      orbitItems.forEach((item) => item.style.setProperty('animation-play-state', next));
    }
    syncOrbit();

    function pauseMedia(scope) {
      scope.querySelectorAll('video, audio, model-viewer').forEach((media) => {
        if (typeof media.pause === 'function') media.pause();
      });
      // Destroy only our deferred external players. Removing their browsing
      // contexts stops playback without relying on a provider API or postMessage.
      scope.querySelectorAll('[data-tpt2-deferred-media]').forEach((template) => {
        template.parentElement.querySelectorAll('iframe').forEach((player) => player.remove());
      });
    }

    function mountExternal(slide) {
      const template = slide?.querySelector('[data-tpt2-deferred-media]');
      if (!template || slide.querySelector('iframe')) return;
      slide.append(template.content.cloneNode(true));
    }

    const galleryStates = galleries.map((gallery) => {
      const slides = [...gallery.querySelectorAll('[data-tpt2-slide]')];
      const dots = [...gallery.querySelectorAll('[data-tpt2-dot]')];
      const state = { gallery, slides, index: 0, visible: !hasObserver };
      const show = (index, moveFocus = false) => {
        if (!slides.length) return;
        state.index = (index + slides.length) % slides.length;
        slides.forEach((slide, i) => {
          const active = i === state.index;
          if (!active) {
            if (slide.contains(document.activeElement)) gallery.focus({ preventScroll: true });
            pauseMedia(slide);
          }
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        dots.forEach((dot, i) => {
          const active = i === state.index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
          dot.tabIndex = active ? 0 : -1;
        });
        if (moveFocus) dots[state.index]?.focus({ preventScroll: true });
        if (state.visible && !document.hidden) mountExternal(slides[state.index]);
      };
      on(gallery.querySelector('[data-tpt2-prev]'), 'click', () => show(state.index - 1));
      on(gallery.querySelector('[data-tpt2-next]'), 'click', () => show(state.index + 1));
      dots.forEach((dot, i) => on(dot, 'click', () => show(i)));
      on(gallery, 'keydown', (event) => {
        // Never consume arrow keys used by a video player, model or form field.
        const target = event.target;
        const isDot = dots.includes(target);
        const isArrow = target.closest?.('[data-tpt2-prev], [data-tpt2-next]');
        if (target !== gallery && !isDot && !isArrow) return;
        let index;
        if (event.key === 'ArrowLeft') index = state.index - 1;
        else if (event.key === 'ArrowRight') index = state.index + 1;
        else if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = slides.length - 1;
        else return;
        event.preventDefault();
        show(index, isDot);
      });
      // A script-triggered play in an inactive slide must not keep playing.
      on(gallery, 'play', (event) => {
        const slide = event.target.closest?.('[data-tpt2-slide]');
        if (slide?.hidden || !state.visible || document.hidden) event.target.pause?.();
      }, { capture: true });
      let touchStart = null;
      on(gallery, 'touchstart', (event) => {
        if (event.touches.length !== 1 || event.target.closest('video, iframe, model-viewer, button')) return;
        touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      }, { passive: true });
      on(gallery, 'touchend', (event) => {
        if (!touchStart || !event.changedTouches.length) return;
        const dx = event.changedTouches[0].clientX - touchStart.x;
        const dy = event.changedTouches[0].clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) show(state.index + (dx < 0 ? 1 : -1));
      }, { passive: true });
      on(gallery, 'touchcancel', () => { touchStart = null; }, { passive: true });
      state.show = show;
      show(0);
      if (hasObserver) {
        const observer = new IntersectionObserver(([entry]) => {
          if (destroyed) return;
          state.visible = entry.isIntersecting;
          if (!state.visible || document.hidden) pauseMedia(gallery);
          else mountExternal(slides[state.index]);
        });
        observer.observe(gallery);
        observers.push(observer);
      }
      return state;
    });

    // Follow the selected variant's media using the same event as the native commerce blocks.
    import('@shopify/events').then(({ StandardEvents }) => {
      if (destroyed) return;
      on(root.closest('.shopify-section') || root, StandardEvents.productSelect, (event) => {
        if (event.target.closest?.('product-card') || !event.promise) return;
        event.promise.then(({ detail }) => {
          if (destroyed) return;
          const mediaId = detail?.resource?.featured_media?.id || detail?.resource?.featured_image?.id;
          if (!mediaId) return;
          galleryStates.forEach((state) => {
            const index = state.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
            if (index >= 0) state.show(index);
          });
        }).catch(() => {});
      });
    }).catch(() => {});

    const activate = (chapter) => {
      if (!chapter) return;
      root.dataset.activeChapter = chapter.dataset.tpt2Chapter || '0';
      chapters.forEach((item) => {
        const active = item === chapter;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-current', active ? 'step' : 'false');
      });
    };
    activate(chapters[0]);

    function resetPointer() {
      pointerPosition = null;
      stage?.style.setProperty('--tpt2-pointer-x', '0');
      stage?.style.setProperty('--tpt2-pointer-y', '0');
    }
    function cancelFrame() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    }
    function update() {
      frame = 0;
      if (destroyed || !visible || document.hidden) return;
      if (story) {
        const rect = story.getBoundingClientRect();
        const stageVisible = rect.top < innerHeight * .92 && rect.bottom > innerHeight * .12;
        root.classList.toggle('is-stage-visible', stageVisible);
        const progress = desktop.matches && !reduced.matches
          ? clamp(-rect.top / Math.max(1, rect.height - innerHeight)) : 0;
        root.style.setProperty('--tpt2-progress', progress.toFixed(4));
        // Choose from all chapters, not only the entries changed by an observer.
        // This remains deterministic when scrolling backwards or jumping sections.
        if (stageVisible && chapters.length) {
          const center = innerHeight * .5;
          let best = chapters[0];
          let distance = Infinity;
          chapters.forEach((chapter) => {
            const box = chapter.getBoundingClientRect();
            const next = box.top > center ? box.top - center : Math.max(0, center - box.bottom);
            if (next < distance) { best = chapter; distance = next; }
          });
          activate(best);
        }
      }
      syncOrbit();
      if (pointerPosition && stage) {
        const rect = stage.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          const x = clamp((pointerPosition.x - rect.left) / rect.width) - .5;
          const y = clamp((pointerPosition.y - rect.top) / rect.height) - .5;
          stage.style.setProperty('--tpt2-pointer-x', x.toFixed(3));
          stage.style.setProperty('--tpt2-pointer-y', y.toFixed(3));
        }
        pointerPosition = null;
      }
    }
    function schedule() {
      if (destroyed || frame || !visible || document.hidden) return;
      frame = requestAnimationFrame(update);
    }
    if (hasObserver) {
      const observer = new IntersectionObserver(([entry]) => {
        if (destroyed) return;
        visible = entry.isIntersecting;
        if (visible) schedule();
        else { cancelFrame(); resetPointer(); pauseMedia(root); syncOrbit(); }
      });
      observer.observe(root);
      observers.push(observer);
    }
    // Capturing document scroll supports scroll containers as well as window scroll.
    // Both events coalesce into one frame; no recurring rAF is scheduled.
    on(document, 'scroll', schedule, { capture: true, passive: true });
    on(window, 'scroll', schedule, { passive: true });
    on(window, 'resize', schedule, { passive: true });
    on(document, 'visibilitychange', () => {
      if (document.hidden) { cancelFrame(); resetPointer(); pauseMedia(root); syncOrbit(); }
      else {
        galleryStates.forEach((state) => {
          if (state.visible) mountExternal(state.slides[state.index]);
        });
        schedule();
      }
    });
    [reduced, desktop, pointer].forEach((query) => on(query, 'change', () => {
      resetPointer();
      if (reduced.matches || !desktop.matches) root.style.setProperty('--tpt2-progress', '0');
      syncOrbit();
      schedule();
    }));
    on(stage, 'pointermove', (event) => {
      if (!visible || document.hidden || !desktop.matches || !pointer.matches || reduced.matches) return;
      pointerPosition = { x: event.clientX, y: event.clientY };
      schedule();
    }, { passive: true });
    on(stage, 'pointerleave', resetPointer);

    root.dataset.theatreV2Ready = 'true';
    root.classList.add('is-js-ready');
    root._tpt2Destroy = () => {
      destroyed = true;
      cancelFrame();
      observers.forEach((observer) => observer.disconnect());
      abort.abort();
      pauseMedia(root);
      resetPointer();
      root.style.removeProperty('--tpt2-progress');
      orbitItems.forEach((item) => item.style.removeProperty('animation-play-state'));
      root.classList.remove('is-js-ready', 'is-stage-visible');
      delete root.dataset.theatreV2Ready;
      delete root._tpt2Destroy;
    };
    schedule();
  }

  function initAll(scope = document) {
    if (scope.matches?.(SELECTOR)) init(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(init);
  }
  window.TotokaPdpTheatreV2 = { init: initAll };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.(SELECTOR)) event.target._tpt2Destroy?.();
    event.target.querySelectorAll?.(SELECTOR).forEach((root) => root._tpt2Destroy?.());
  });
})();
